import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, forkJoin, of, ReplaySubject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GoogleMapService } from '@nimbus-library/features/shared/services/google-map/google-map.service';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-google-map-widget',
  templateUrl: './google-map-widget.component.html',
  styleUrls: ['./google-map-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleMapWidgetComponent implements AfterViewInit, OnChanges {
  @Input() public address: string;
  @Input() public usageLocation: string;

  @Output() public errorMapDisplaying = new EventEmitter<string>();

  @ViewChild('gmap') public gmapElement: ElementRef;

  private addressData$: ReplaySubject<{ address: string; usageLocation: string }> = new ReplaySubject(1);

  public constructor(
    private readonly googleMapService: GoogleMapService,
    private readonly store: Store,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.address || changes.usageLocation) {
      this.addressData$.next({ address: this.address, usageLocation: this.usageLocation });
    }
  }

  public ngAfterViewInit(): void {
    combineLatest([this.store.select(CommonThemeState.theme).pipe(distinctUntilChanged()), this.addressData$])
      .pipe(
        switchMap(([theme, addressData]) =>
          forkJoin([
            of(theme),
            this.googleMapService.load().pipe(
              take(1),
              switchMap(() => this.googleMapService.findLocation(addressData.address, addressData.usageLocation)),
            ),
          ]),
        ),
        map(([theme, location]) => this.googleMapService.createMap(this.gmapElement.nativeElement, location, theme)),
        catchError((error) => {
          this.errorMapDisplaying.emit(error);
          return of(error);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
